import { useState } from "react";
import { SlidersHorizontal, X, Search, Download, Check } from "lucide-react";
import { useFilter, PRESETS } from "@/context/FilterContext";
import { useMasters } from "@/lib/useMasters";
import { useAuth } from "@/context/AuthContext";
import client, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";

function MultiSelect({ label, options, selected, onChange, testid, labelKey = "display_name" }) {
  const [open, setOpen] = useState(false);
  const toggle = (id) =>
    selected.includes(id) ? onChange(selected.filter((x) => x !== id)) : onChange([...selected, id]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid={`filter-${testid}`}
          className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm rounded-md border bg-white hover:border-violet-400 transition-colors"
          style={{ borderColor: "#E2E8F0" }}
        >
          <span className="truncate text-slate-700">
            {label}
            {selected.length > 0 && (
              <span className="ml-1.5 text-xs font-semibold text-violet-700">({selected.length})</span>
            )}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <div className="max-h-72 overflow-y-auto p-1">
          {(options || []).map((o) => (
            <button
              key={o.id}
              onClick={() => toggle(o.id)}
              data-testid={`filter-${testid}-opt`}
              className="w-full flex items-center gap-2 px-2.5 py-2 text-sm rounded hover:bg-slate-100 text-left"
            >
              <span
                className="w-4 h-4 rounded border flex items-center justify-center shrink-0"
                style={{ borderColor: selected.includes(o.id) ? "#7C3AED" : "#CBD5E1", background: selected.includes(o.id) ? "#7C3AED" : "#fff" }}
              >
                {selected.includes(o.id) && <Check size={12} color="#fff" />}
              </span>
              <span className="truncate">{o[labelKey]}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function GlobalFilterBar({ meta }) {
  const { filter, update, reset, activeCount, query } = useFilter();
  const { can } = useAuth();
  const { data: masters } = useMasters();
  const [sheetOpen, setSheetOpen] = useState(false);

  const setPreset = (p) => update({ preset: p.key, ...p.range() });

  const doExport = async () => {
    try {
      const res = await client.get(`/exports/nesting-records?${query}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Catworth_Laser_Nesting_Export_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  const toggleStatus = (s) =>
    filter.statuses.includes(s)
      ? update({ statuses: filter.statuses.filter((x) => x !== s) })
      : update({ statuses: [...filter.statuses, s] });

  return (
    <div className="px-4 sm:px-6 py-2.5 border-t flex items-center gap-2 flex-wrap bg-slate-50" style={{ borderColor: "#E2E8F0" }}>
      {/* presets */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            onClick={() => setPreset(p)}
            data-testid={`preset-${p.key}`}
            className="text-xs font-medium px-2.5 py-1.5 rounded-full border transition-colors"
            style={
              filter.preset === p.key
                ? { background: "#0F172A", color: "#fff", borderColor: "#0F172A" }
                : { background: "#fff", color: "#475569", borderColor: "#E2E8F0" }
            }
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* search */}
      <div className="relative flex-1 min-w-[160px] max-w-xs">
        <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          data-testid="global-search"
          placeholder="Search records…"
          value={filter.search}
          onChange={(e) => update({ search: e.target.value })}
          className="pl-8 h-9 bg-white"
        />
      </div>

      <div className="flex items-center gap-2 ml-auto">
        {meta != null && <span className="text-xs text-slate-500 hidden sm:inline" data-testid="filter-meta">{meta}</span>}

        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="sm" className="h-9 bg-white" data-testid="open-filters-btn">
              <SlidersHorizontal size={15} className="mr-1.5" /> Filters
              {activeCount > 0 && (
                <span className="ml-1.5 text-[10px] font-bold text-white rounded-full px-1.5 py-0.5" style={{ background: "#7C3AED" }}>
                  {activeCount}
                </span>
              )}
            </Button>
          </SheetTrigger>
          <SheetContent className="w-full sm:max-w-md overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="font-head">Global Filters</SheetTitle>
              <SheetDescription className="sr-only">Refine all dashboards and the record ledger by date, customer, machine, grade, ownership, thickness, programmer and status.</SheetDescription>
            </SheetHeader>
            <div className="mt-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">From</label>
                  <Input type="date" value={filter.from} onChange={(e) => update({ from: e.target.value, preset: "custom" })} data-testid="filter-from" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">To</label>
                  <Input type="date" value={filter.to} onChange={(e) => update({ to: e.target.value, preset: "custom" })} data-testid="filter-to" />
                </div>
              </div>

              <MultiSelect label="Customer" testid="customer" options={masters?.customers} selected={filter.customerIds} onChange={(v) => update({ customerIds: v })} />
              <MultiSelect label="Machine" testid="machine" options={masters?.machines} selected={filter.machineIds} onChange={(v) => update({ machineIds: v })} />
              <MultiSelect label="Material grade" testid="grade" options={masters?.grades} selected={filter.gradeIds} onChange={(v) => update({ gradeIds: v })} />
              <MultiSelect label="Material ownership" testid="ownership" options={masters?.ownership} selected={filter.materialOwnershipIds} onChange={(v) => update({ materialOwnershipIds: v })} />
              <MultiSelect label="Thickness" testid="thickness" options={masters?.thicknesses} selected={filter.thicknessIds} onChange={(v) => update({ thicknessIds: v })} />
              <MultiSelect label="Programmer" testid="programmer" options={masters?.programmers} selected={filter.programmerIds} onChange={(v) => update({ programmerIds: v })} />

              <div>
                <label className="text-xs font-medium text-slate-600 mb-1.5 block">Status</label>
                <div className="flex gap-2 flex-wrap">
                  {["active", "corrected", "voided"].map((s) => (
                    <button key={s} onClick={() => toggleStatus(s)} data-testid={`filter-status-${s}`}
                      className="text-xs font-medium px-3 py-1.5 rounded-full border capitalize transition-colors"
                      style={filter.statuses.includes(s)
                        ? { background: "#7C3AED", color: "#fff", borderColor: "#7C3AED" }
                        : { background: "#fff", color: "#475569", borderColor: "#E2E8F0" }}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-2 pt-3">
                <Button variant="outline" className="flex-1" onClick={reset} data-testid="reset-filters-btn">
                  <X size={15} className="mr-1" /> Reset
                </Button>
                <Button className="flex-1 text-white" style={{ background: "#0D9488" }} onClick={() => setSheetOpen(false)} data-testid="apply-filters-btn">
                  Apply
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>

        {can("analytics.export") && (
          <Button variant="outline" size="sm" className="h-9 bg-white" onClick={doExport} data-testid="export-btn">
            <Download size={15} className="mr-1.5" /> <span className="hidden sm:inline">Export</span>
          </Button>
        )}
      </div>
    </div>
  );
}
